import React, { useState, useMemo } from 'react';
import { generateOrderedWindowsShopPDF } from '../services/ordered-windows-shop-pdf';

const BROWN = '#8B4513';
const TEAL  = '#00695C';

// Only ordered windows and doors — no TWP cutlist windows, no adders
function isOrderedItem(w) {
  if (w.lineType === 'twp_window') return false;
  if (w.lineType === 'adder' || w.type === 'adder') return false;
  const desc = (w.style || w.description || '').toLowerCase();
  if (w.lineType === 'door') return true;
  if (['door', 'patio door', 'storm door', 'exterior door', 'sliding door', 'side lite'].some(d => desc.includes(d))) return true;
  if (w.lineType === 'ordered_window') return true;
  // Fallback: has dimensions and is not a TWP window
  if (w.width || w.height) return true;
  return false;
}

function getTypeTag(w) {
  const desc = (w.style || w.description || '').toLowerCase();
  if (w.lineType === 'door' || ['door', 'patio door', 'storm door', 'exterior door', 'sliding door', 'side lite'].some(d => desc.includes(d))) return 'DOOR';
  return 'ORDERED WINDOW';
}

export default function OrderedWindowsShopOrderModal({ jobOrder, estimate, onClose, onPreview }) {
  const customer       = jobOrder?.customer || estimate?.customer || {};
  const contractNumber = jobOrder?.contractNumber || '';

  const allItems = useMemo(() => {
    return (jobOrder?.windows || [])
      .filter(isOrderedItem)
      .map((w, i) => ({ ...w, _modalIdx: i }));
  }, [jobOrder]);

  const [selected, setSelected] = useState(() => {
    const s = {};
    allItems.forEach((_, i) => { s[i] = true; });
    return s;
  });
  const [generating, setGenerating] = useState(false);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  function toggleAll(val) {
    const s = {};
    allItems.forEach((_, i) => { s[i] = val; });
    setSelected(s);
  }

  async function handleGenerate() {
    setGenerating(true);
    try {
      const chosenItems = allItems.filter((_, i) => selected[i]);
      const blob = await generateOrderedWindowsShopPDF(
        { ...jobOrder, customer },
        chosenItems,
        true
      );
      const fname = `ShopOrder_Ordered_${contractNumber}_${(customer.name || 'Customer').replace(/\s+/g, '_')}.pdf`;
      onPreview(blob, fname);
    } catch (e) {
      alert('Error generating PDF: ' + e.message);
    }
    setGenerating(false);
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 2000, padding: '20px',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'white', borderRadius: '16px', width: '100%', maxWidth: '660px',
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${TEAL} 0%, #004d40 100%)`,
          padding: '20px 24px', color: 'white',
        }}>
          <div style={{ fontSize: '17px', fontWeight: '800', marginBottom: '3px' }}>
            Ordered Windows — Shop Order
          </div>
          <div style={{ fontSize: '13px', opacity: 0.85 }}>
            Contract #{contractNumber} — {customer.name}
          </div>
          <div style={{ fontSize: '11px', opacity: 0.65, marginTop: '3px' }}>
            Select which ordered windows and doors to include. No prices will appear on the PDF.
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 24px' }}>

          {allItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#aaa' }}>
              <div style={{ fontSize: '15px', marginBottom: '8px' }}>No ordered windows or doors found.</div>
              <div style={{ fontSize: '12px' }}>
                Ordered products are non-TWP items (ProVia, Pella, Minnkota, Vector, etc.).<br />
                TWP cutlist windows are excluded from this shop order.
              </div>
            </div>
          ) : (
            <>
              {/* Select all / none row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <button onClick={() => toggleAll(true)}  style={pill(TEAL)}>Select All</button>
                <button onClick={() => toggleAll(false)} style={pill('#999')}>Deselect All</button>
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>
                  {selectedCount} of {allItems.length} selected
                </span>
              </div>

              {/* Item list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {allItems.map((w, i) => {
                  const isChecked  = !!selected[i];
                  const typeTag    = getTypeTag(w);
                  const desc       = w.style || w.description || '—';
                  const sizeStr    = w.width && w.height
                    ? `${w.width}" × ${w.height}"`
                    : w.width ? `${w.width}"` : w.height ? `${w.height}"` : '';
                  const tagColor   = typeTag === 'DOOR' ? '#1565C0' : TEAL;

                  return (
                    <label
                      key={i}
                      style={{
                        display: 'flex', gap: '12px', alignItems: 'flex-start',
                        padding: '12px 14px', borderRadius: '10px', cursor: 'pointer',
                        border: `2px solid ${isChecked ? TEAL : '#e5e5e5'}`,
                        background: isChecked ? '#f0faf8' : '#fafafa',
                        transition: 'border-color 0.12s, background 0.12s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={e => setSelected(p => ({ ...p, [i]: e.target.checked }))}
                        style={{ width: '17px', height: '17px', accentColor: TEAL, marginTop: '2px', flexShrink: 0 }}
                      />
                      <div style={{ flex: 1 }}>
                        {/* Top row: description + type tag + size + qty */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '5px' }}>
                          <span style={{ fontWeight: '800', fontSize: '14px', color: '#222' }}>{desc}</span>
                          <span style={{
                            fontSize: '9px', fontWeight: '800', color: tagColor,
                            background: tagColor + '18', padding: '2px 7px',
                            borderRadius: '20px', letterSpacing: '0.5px',
                          }}>{typeTag}</span>
                          {sizeStr && (
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#444' }}>{sizeStr}</span>
                          )}
                          {w.qty > 1 && (
                            <span style={{ fontSize: '11px', color: '#888' }}>× {w.qty}</span>
                          )}
                        </div>
                        {/* Details row */}
                        <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: '11px', color: '#777' }}>
                          {w.room         && <span><strong style={{ color: '#555' }}>Room:</strong> {w.room}</span>}
                          {w.colorOutside && <span><strong style={{ color: '#555' }}>Out:</strong> {w.colorOutside}</span>}
                          {w.colorInside  && <span><strong style={{ color: '#555' }}>In:</strong> {w.colorInside}</span>}
                          {w.glassPkg     && <span><strong style={{ color: '#555' }}>Glass:</strong> {w.glassPkg}</span>}
                          {w.vendor       && <span><strong style={{ color: TEAL }}>Vendor:</strong> {w.vendor}</span>}
                          {w.extrasNotes  && (
                            <span style={{ fontStyle: 'italic', color: '#888' }}>{w.extrasNotes}</span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #eee',
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
          background: '#f9fffe',
        }}>
          <button onClick={onClose} style={pill('#aaa')}>Cancel</button>
          <button
            onClick={handleGenerate}
            disabled={generating || selectedCount === 0}
            style={{
              background: selectedCount === 0 ? '#ccc' : TEAL,
              color: 'white', border: 'none', borderRadius: '10px',
              padding: '10px 26px', fontSize: '14px', fontWeight: '700',
              cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              opacity: selectedCount === 0 ? 0.6 : 1,
            }}
          >
            {generating ? 'Generating...' : `Generate Shop Order (${selectedCount} item${selectedCount !== 1 ? 's' : ''})`}
          </button>
        </div>

      </div>
    </div>
  );
}

function pill(color) {
  return {
    background: color, color: 'white', border: 'none',
    borderRadius: '8px', padding: '8px 18px', fontSize: '12px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
  };
}